import React from 'react'
import { CalendarIcon } from 'lucide-react'
import { format } from 'date-fns'
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@radix-ui/react-popover'

import { cn } from 'src/utils'
import { DistanceMetrics } from 'src/types'
import { carMakes, EngineTypes } from 'src/constants'

import Container from 'src/components/Container'

import { useAutoInfoForm } from 'src/pages/AutoInfoForm/useAutoInfoForm'
import { DISTANCE_METRICS } from 'src/features/UserCurrency/constants'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/shadcn/select'
import { Input } from 'src/components/shadcn/input'
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Form,
  FormDescription,
} from 'src/components/shadcn/form'
import { Checkbox } from 'src/components/shadcn/checkbox'
import { Card, CardContent, CardHeader } from 'src/components/shadcn/card'
import { Calendar } from 'src/components/shadcn/calendar'
import { Button } from 'src/components/shadcn/button'
import { DEFAULT_DATE, FORMAT_PPP, INPUT_FIELDS } from './constants'

const AutoInfoForm = () => {
  const {
    locale,
    autoInfoForm,
    setBrand,
    onSubmit,
    isLoadingGetModelsForMake,
    modelsForMake,
    isLoadingAddCar,
    fuelTypeDictionary,
    getFormattedTitle,
  } = useAutoInfoForm()

  return (
    <>
      <Container classes="flex justify-center py-6">
        <Card className="w-full sm:w-2/4 rounded-xl">
          <CardHeader className="p-6 border-b mb-2">
            <h1 className="text-2xl font-bold text-center">
              {locale.cardTitle}
            </h1>
          </CardHeader>
          <CardContent>
            <Form {...autoInfoForm}>
              <form onSubmit={autoInfoForm.handleSubmit(onSubmit)}>
                <div>
                  <FormField
                    control={autoInfoForm.control}
                    name="carBrand"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{locale.carBrand}</FormLabel>
                        <FormControl>
                          <Select
                            onValueChange={value => {
                              field.onChange(value)
                              setBrand(value)
                            }}
                            defaultValue={field.value}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue
                                placeholder={locale.carBrandSelect}
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {carMakes.map(make => (
                                <SelectItem key={make} value={make}>
                                  {make}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={autoInfoForm.control}
                    name="carModel"
                    render={({ field }) => (
                      <FormItem className="mt-5">
                        <FormLabel>{locale.carModel}</FormLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <SelectTrigger className="w-full">
                              {isLoadingGetModelsForMake && <p>Loading...</p>}
                              {!isLoadingGetModelsForMake && (
                                <SelectValue
                                  placeholder={locale.carModelSelect}
                                />
                              )}
                            </SelectTrigger>
                            <SelectContent>
                              {modelsForMake?.map(model => (
                                <SelectItem key={model} value={model}>
                                  {model}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {INPUT_FIELDS.map(field => (
                    <FormField
                      key={field}
                      control={autoInfoForm.control}
                      name={field}
                      render={({ field: formField }) => (
                        <FormItem className="mt-5">
                          <FormLabel>
                            {getFormattedTitle(field, locale[`${field}`])}
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="text"
                              placeholder={locale[`${field}_placeholder`]}
                              {...formField}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ))}
                  <FormField
                    control={autoInfoForm.control}
                    name="distanceMetric"
                    render={({ field }) => (
                      <FormItem className="mt-4">
                        <FormLabel>{locale.distanceMetric}</FormLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder={field.value} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                {DISTANCE_METRICS.map(metric => {
                                  return (
                                    <SelectItem
                                      className="lowercase"
                                      key={metric}
                                      value={DistanceMetrics[metric]}
                                    >
                                      {metric}
                                    </SelectItem>
                                  )
                                })}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={autoInfoForm.control}
                    name="engineType"
                    render={({ field }) => (
                      <FormItem className="mt-4">
                        <FormLabel>{locale.engineTypeTitle}</FormLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select an engine type" />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.values(EngineTypes).map(type => (
                                <SelectItem key={type} value={type}>
                                  {fuelTypeDictionary[type]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="gap-2 sm:flex justify-between align-middle">
                    <FormField
                      control={autoInfoForm.control}
                      name="lastServiceDate"
                      render={({ field }) => (
                        <FormItem className="flex flex-col mt-5">
                          <FormLabel>{locale.lastServiceDate}</FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant="outline"
                                  className={cn(
                                    'w-full pl-3 text-left font-normal',
                                    !field.value && 'text-muted-foreground',
                                  )}
                                >
                                  {field.value ? (
                                    format(field.value, FORMAT_PPP)
                                  ) : (
                                    <span>{locale.lastServiceDate}</span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-auto p-0 rounded-md shadow-lg"
                              align="start"
                            >
                              <Calendar
                                mode="single"
                                className="rounded-md border shadow bg-[#1d283a] p-4"
                                selected={field.value}
                                onSelect={field.onChange}
                                disabled={(date: Date) =>
                                  date > new Date() ||
                                  date < new Date(DEFAULT_DATE)
                                }
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                          <FormDescription>
                            {locale.lastServiceDatePlaceholder}
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={autoInfoForm.control}
                      name="nextMaintenance"
                      render={({ field }) => (
                        <FormItem className="flex flex-col mt-5">
                          <FormLabel>{locale.nextMaintenance}</FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant="outline"
                                  className={cn(
                                    'w-full pl-3 text-left font-normal',
                                    !field.value && 'text-muted-foreground',
                                  )}
                                >
                                  {field.value ? (
                                    format(field.value, FORMAT_PPP)
                                  ) : (
                                    <span>Pick a date</span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-auto p-0"
                              align="start"
                            >
                              <Calendar
                                mode="single"
                                className="rounded-md border bg-[#1d283a]"
                                selected={field.value}
                                onSelect={field.onChange}
                                disabled={(date: Date) => date < new Date()}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                          <FormDescription>
                            {locale.nextMaintenancePlaceholder}
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={autoInfoForm.control}
                    name="isMainCar"
                    render={({ field }) => (
                      <FormItem className="mt-6 flex items-center justify-between">
                        <FormLabel className="text-xl font-bold">
                          {locale.mainCar}
                        </FormLabel>
                        <FormControl className="!mt-0">
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={checked => field.onChange(checked)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="mt-8 w-full justify-center flex">
                  <Button
                    disabled={isLoadingAddCar || isLoadingGetModelsForMake}
                    className="w-[250px] justify-center rounded-xl"
                    type="submit"
                  >
                    {locale.button}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </Container>
    </>
  )
}

export default AutoInfoForm
